// Renders a prayer/message onto a canvas as a beautifully-designed
// social-shareable PNG (1080x1350 — Instagram/Story friendly).
// Uses Web Share API when available (mobile), else downloads the PNG.

const BRAND = {
  cream: '#FDFBF7',
  paper: '#F3EFE7',
  ink: '#2C2A29',
  muted: '#5C564D',
  sage: '#78A18E',
  sageDark: '#5C715E',
  gold: '#E8C265',
  peach: '#E57A44',
};

function wrapText(ctx, text, maxWidth) {
  const paragraphs = String(text || '').split(/\n+/);
  const lines = [];
  for (const para of paragraphs) {
    const words = para.split(/\s+/);
    let line = '';
    for (const word of words) {
      const test = line ? `${line} ${word}` : word;
      const w = ctx.measureText(test).width;
      if (w > maxWidth && line) {
        lines.push(line);
        line = word;
      } else {
        line = test;
      }
    }
    if (line) lines.push(line);
    lines.push(''); // paragraph break
  }
  while (lines.length && lines[lines.length - 1] === '') lines.pop();
  return lines;
}

function drawRoundedRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function drawHeart(ctx, cx, cy, size, color) {
  ctx.save();
  ctx.fillStyle = color;
  ctx.beginPath();
  const s = size;
  ctx.moveTo(cx, cy + s * 0.3);
  ctx.bezierCurveTo(cx, cy, cx - s * 0.5, cy, cx - s * 0.5, cy + s * 0.3);
  ctx.bezierCurveTo(cx - s * 0.5, cy + s * 0.6, cx, cy + s * 0.85, cx, cy + s);
  ctx.bezierCurveTo(cx, cy + s * 0.85, cx + s * 0.5, cy + s * 0.6, cx + s * 0.5, cy + s * 0.3);
  ctx.bezierCurveTo(cx + s * 0.5, cy, cx, cy, cx, cy + s * 0.3);
  ctx.fill();
  ctx.restore();
}

function renderPrayerCanvas({ content, name, isSanctuary, prayedCount, date }) {
  const W = 1080, H = 1350;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');

  // Cream backdrop with subtle gradient
  const bg = ctx.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, BRAND.cream);
  bg.addColorStop(1, BRAND.paper);
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  // Decorative soft blobs
  ctx.save();
  ctx.globalAlpha = 0.18;
  ctx.fillStyle = BRAND.sage;
  ctx.beginPath(); ctx.arc(-80, -60, 320, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = BRAND.gold;
  ctx.beginPath(); ctx.arc(W + 100, H - 200, 380, 0, Math.PI * 2); ctx.fill();
  ctx.restore();

  // Inner card
  const cardPad = 60;
  const cardX = 60, cardY = 200, cardW = W - 120, cardH = H - 340;
  ctx.save();
  ctx.shadowColor = 'rgba(44, 39, 36, 0.12)';
  ctx.shadowBlur = 40;
  ctx.shadowOffsetY = 12;
  ctx.fillStyle = '#FFFFFF';
  drawRoundedRect(ctx, cardX, cardY, cardW, cardH, 40);
  ctx.fill();
  ctx.restore();

  // Brand header
  const heartX = 100, heartY = 90;
  ctx.save();
  drawRoundedRect(ctx, heartX, heartY, 64, 64, 16);
  ctx.fillStyle = BRAND.peach;
  ctx.fill();
  drawHeart(ctx, heartX + 32, heartY + 18, 30, '#FFFFFF');
  ctx.restore();

  ctx.fillStyle = BRAND.ink;
  ctx.font = 'bold 44px "Playfair Display", Georgia, serif';
  ctx.textBaseline = 'middle';
  ctx.fillText('Love Restored', heartX + 84, heartY + 32);

  ctx.fillStyle = BRAND.muted;
  ctx.font = '500 22px Manrope, sans-serif';
  ctx.fillText('A Prayer From Our Wall', heartX + 84, heartY + 66);

  // Section label
  ctx.textBaseline = 'top';
  ctx.fillStyle = BRAND.sage;
  ctx.font = '700 20px Manrope, sans-serif';
  ctx.fillText('LIFT THIS UP IN PRAYER', cardX + cardPad, cardY + 50);

  // Divider
  ctx.fillStyle = BRAND.paper;
  ctx.fillRect(cardX + cardPad, cardY + 90, 80, 3);

  // Body text — wrap
  const bodyX = cardX + cardPad;
  const bodyMaxW = cardW - cardPad * 2;
  const bodyTop = cardY + 130;

  // Choose font size that fits
  let fontSize = 44;
  let lines = [];
  const maxHeight = cardH - 260;
  while (fontSize >= 26) {
    ctx.font = `italic 500 ${fontSize}px "Lora", Georgia, serif`;
    lines = wrapText(ctx, content, bodyMaxW);
    const total = lines.length * fontSize * 1.45;
    if (total <= maxHeight) break;
    fontSize -= 2;
  }
  ctx.fillStyle = BRAND.ink;
  ctx.textBaseline = 'top';
  const lineH = fontSize * 1.45;
  lines.forEach((ln, i) => {
    ctx.fillText(ln, bodyX, bodyTop + i * lineH);
  });

  // Author footer inside card
  const footerY = cardY + cardH - 90;
  ctx.fillStyle = BRAND.paper;
  ctx.fillRect(cardX + cardPad, footerY - 22, cardW - cardPad * 2, 2);

  ctx.fillStyle = BRAND.muted;
  ctx.font = '600 26px Manrope, sans-serif';
  ctx.textBaseline = 'middle';
  const nameStr = name || 'A friend';
  ctx.fillText(`— ${nameStr}`, cardX + cardPad, footerY + 6);

  // Sanctuary badge
  if (isSanctuary) {
    const badgeText = '✧ Sanctuary';
    ctx.font = '700 20px Manrope, sans-serif';
    const bw = ctx.measureText(badgeText).width + 28;
    const bx = cardX + cardPad + ctx.measureText(`— ${nameStr}`).width + 24;
    const by = footerY - 8;
    ctx.fillStyle = 'rgba(120, 161, 142, 0.15)';
    drawRoundedRect(ctx, bx, by, bw, 32, 16);
    ctx.fill();
    ctx.fillStyle = BRAND.sageDark;
    ctx.fillText(badgeText, bx + 14, by + 16);
  }

  // Prayed count pill (right side of footer)
  if (prayedCount && prayedCount > 0) {
    const pillText = `${prayedCount} praying`;
    ctx.font = '600 22px Manrope, sans-serif';
    const pw = ctx.measureText(pillText).width + 44;
    const px = cardX + cardW - cardPad - pw;
    const py = footerY - 8;
    ctx.fillStyle = 'rgba(120, 161, 142, 0.15)';
    drawRoundedRect(ctx, px, py, pw, 32, 16);
    ctx.fill();
    drawHeart(ctx, px + 18, py + 8, 16, BRAND.sage);
    ctx.fillStyle = BRAND.sageDark;
    ctx.fillText(pillText, px + 32, py + 16);
  }

  // Bottom brand strip
  ctx.textBaseline = 'middle';
  ctx.fillStyle = BRAND.muted;
  ctx.font = '500 22px Manrope, sans-serif';
  const bottomY = H - 60;
  ctx.textAlign = 'center';
  ctx.fillText('loverestored.net  ·  Restore Your Relationships Through Faith', W / 2, bottomY);
  ctx.textAlign = 'left';

  return canvas;
}

function canvasToBlob(canvas) {
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/png', 0.95));
}

export async function sharePrayerAsImage(prayer) {
  const canvas = renderPrayerCanvas({
    content: prayer.content,
    name: prayer.name || 'A friend',
    isSanctuary: !!prayer.is_sanctuary_member,
    prayedCount: prayer.prayed_count || 0,
    date: prayer.created_at,
  });

  const blob = await canvasToBlob(canvas);
  if (!blob) throw new Error('Could not create image');

  const fileName = `love-restored-prayer-${(prayer.id || 'shared').slice(0, 8)}.png`;
  const file = new File([blob], fileName, { type: 'image/png' });

  // Try native share sheet first (mobile)
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({
        files: [file],
        title: 'A Prayer From Love Restored',
        text: 'Would you lift this up in prayer? · loverestored.net',
      });
      return { shared: true };
    } catch (err) {
      if (err && err.name === 'AbortError') return { shared: false, cancelled: true };
      // fall through to download
    }
  }

  // Fallback: download the PNG
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 5000);
  return { shared: false, downloaded: true };
}
